import { config } from '../config';
import {
  parseAcceptedInvitation,
  parseCreatedInvitation,
  parseOnboardingResult,
  parseOrgRoster,
} from '../lib/hbs-envelope';
import type { MembershipRole } from '../lib/invite';
import { parseMemberships, type Membership } from '../lib/memberships';

/**
 * Panggilan LANGSUNG ke HBS — bukan lewat Workforce API.
 *
 * Pola yang sama dengan `GET /me/memberships` di layar masuk, dan alasannya sama: pertanyaannya
 * milik HBS. Workforce API tidak dapat menjawabnya — seluruh route-nya menuntut
 * `x-organization-id` dan konteks tenant, sedangkan undangan justru mendahului keanggotaan.
 *
 * Prasyarat operasional: origin Workforce harus terdaftar di `CORS_ORIGINS` milik HBS API —
 * sudah, karena layar masuk sudah memanggilnya.
 */

export class HbsError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Kode domain HBS (`error.code`, docs/06) — `undefined` bila badan tak terbaca atau tak
     *  membawanya. Dipakai pemanggil yang perlu membedakan sebab di status HTTP yang SAMA. */
    readonly code?: string,
  ) {
    super(message);
    this.name = 'HbsError';
  }
}

/** Pesan HBS diambil apa adanya bila ada; ia menyebut hal yang layar ini tidak tahu. */
async function bacaGalat(response: Response): Promise<never> {
  let pesan = `HBS menolak (${response.status}).`;
  let kode: string | undefined;
  try {
    const body: unknown = await response.json();
    const rec = body as { message?: unknown; error?: { message?: unknown; code?: unknown } };
    const dari =
      typeof rec?.message === 'string'
        ? rec.message
        : typeof rec?.error?.message === 'string'
          ? rec.error.message
          : null;
    if (dari) pesan = dari;
    if (typeof rec?.error?.code === 'string') kode = rec.error.code;
  } catch {
    // Badan bukan JSON — pertahankan pesan bawaan. Melempar di sini akan menyembunyikan
    // status HTTP yang justru satu-satunya keterangan yang tersisa.
  }
  throw new HbsError(response.status, pesan, kode);
}

export interface CreatedInvitation {
  readonly id: string;
  readonly email: string;
  /** Token plaintext, dikembalikan HANYA SEKALI oleh HBS. Tidak pernah dapat diambil lagi. */
  readonly token: string;
}

export async function inviteMember(
  accessToken: string,
  organizationId: string,
  body: { readonly email: string; readonly membershipRole: MembershipRole },
): Promise<CreatedInvitation> {
  const response = await fetch(
    `${config.hbsApiBaseUrl}/organizations/${encodeURIComponent(organizationId)}/invitations`,
    {
      method: 'POST',
      headers: {
        authorization: `Bearer ${accessToken}`,
        'content-type': 'application/json',
        accept: 'application/json',
      },
      /*
       * `redirectTo` — supaya EMAIL undangannya sendiri mendarat di Workforce, bukan di CRM.
       *
       * Sebelum ini, admin harus menyalin tautan dari layar dan mengirimnya manual, karena email
       * HBS selalu menunjuk `APP_PUBLIC_URL` (CRM). Itu memindahkan beban ke manusia, dan orang
       * yang tidak akan pernah memakai CRM tetap diseret ke sana.
       *
       * Server memvalidasinya terhadap daftar-izin; nilai di luar daftar diabaikan dan tautannya
       * kembali ke perilaku lama — jadi ini tidak dapat dipakai mengarahkan token ke mana pun.
       */
      body: JSON.stringify({ ...body, redirectTo: window.location.origin }),
    },
  );
  if (!response.ok) await bacaGalat(response);
  /*
   * Dibaca lewat `parseCreatedInvitation`, BUKAN langsung dari badan respons.
   *
   * Pembacaan lama menuntut `token`, `id`, dan `email` berada di lapisan atas. Ketiganya tidak
   * di sana: HBS membungkus setiap respons sukses jadi `{ data: ... }`, dan di dalamnya `id`
   * serta `email` masih bersarang lagi di bawah `invitation`. Akibatnya SETIAP undangan yang
   * berhasil dilaporkan sebagai "HBS tidak mengembalikan token" — menakutkan, dan salah.
   * Diukur langsung terhadap produksi 21 Agu 2026.
   */
  const dibuat = parseCreatedInvitation(await response.json(), body.email);
  if (dibuat === null) {
    // Tanpa token, tautan Workforce tidak dapat dibangun — dan undangannya SUDAH terkirim.
    // Dikatakan apa adanya, bukan dilaporkan sebagai gagal.
    throw new HbsError(
      response.status,
      'Undangan terkirim, tetapi HBS tidak mengembalikan token — tautan Workforce tidak dapat dibuat. Orangnya tetap dapat memakai tautan dari email.',
    );
  }
  return dibuat;
}

/** `POST /invitations/accept` — endpoint PUBLIK; dipanggil tanpa token sesi. */
export async function acceptInvitation(body: {
  readonly token: string;
  readonly fullName?: string;
  readonly password?: string;
}): Promise<{ readonly userId: string }> {
  const response = await fetch(`${config.hbsApiBaseUrl}/invitations/accept`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) await bacaGalat(response);
  return parseAcceptedInvitation(await response.json());
}

/**
 * Daftar keanggotaan organisasi — dipanggil LANGSUNG ke HBS, tanpa `x-organization-id`.
 *
 * ADA KARENA nama organisasi hanya pernah diambil SEKALI, di layar masuk, lalu dibuang begitu
 * organisasinya dipilih — `MeResponse` cuma membawa `organizationId` (UUID). Header (item 12,
 * review 12 Sep 2026) butuh nama yang sama, jadi diambil ulang lewat panggilan yang SAMA persis
 * dengan yang dipakai `SignInScreen`, bukan endpoint baru.
 */
export async function fetchMemberships(accessToken: string): Promise<readonly Membership[]> {
  const response = await fetch(`${config.hbsApiBaseUrl}/me/memberships`, {
    headers: { accept: 'application/json', authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) await bacaGalat(response);
  const payload: unknown = await response.json();
  // HBS membungkus daftar dalam envelope pada sebagian rute; terima kedua bentuk.
  const daftar =
    Array.isArray(payload) || payload === null
      ? payload
      : ((payload as { data?: unknown }).data ?? payload);
  return parseMemberships(daftar);
}

/**
 * Roster anggota organisasi — dipanggil LANGSUNG ke HBS.
 *
 * `GET /organizations/:id/members` di HBS SENGAJA tidak ber-`@Roles`: setiap anggota aktif boleh
 * memanggilnya (guard-nya keanggotaan, bukan peran). Karena itu kurir pun dapat mengetahui
 * namanya sendiri tanpa Workforce API menumbuhkan endpoint baru dan tanpa deploy.
 *
 * Field yang dikembalikan HBS sengaja tidak memuat email maupun telepon (ADR-0025).
 */
export async function fetchOrgRoster(
  accessToken: string,
  organizationId: string,
): Promise<readonly { readonly id: string; readonly displayName: string }[]> {
  const response = await fetch(
    `${config.hbsApiBaseUrl}/organizations/${encodeURIComponent(organizationId)}/members`,
    { headers: { authorization: `Bearer ${accessToken}`, accept: 'application/json' } },
  );
  if (!response.ok) await bacaGalat(response);
  return parseOrgRoster(await response.json());
}

/**
 * Trial 14 hari Workforce TERSENDIRI (BLD-015 lanjutan, 2 Okt 2026) — Langkah KEDUA: buat
 * organisasi.
 *
 * `POST /onboarding` di HBS SUDAH ADA & SUDAH berjalan sejak 13 Sep 2026 (ADR-0045 §3C D2,
 * pendaftaran mandiri CRM) — dipanggil LANGSUNG dari sini, pola yang SAMA dengan
 * `/me/memberships` di atas, prasyarat CORS yang sama pula (sudah terdaftar). HBS tidak tahu ia
 * dipanggil dari Workforce; organisasi yang lahir adalah organisasi `customer` SUNGGUHAN dengan
 * trial 14 hari `LanggananService.mulaiTrial` yang sama persis seperti pendaftar CRM — BUKAN
 * organisasi demo. Kosong (bukan pre-seeded) secara sengaja: trial sungguhan untuk data sungguhan
 * pelanggan sendiri, bukan pratinjau data contoh (beda tujuan dari Demo Instan yang baru dihapus).
 */
export async function startTrial(body: {
  readonly organizationName: string;
  readonly slug: string;
  readonly accessToken: string;
}): Promise<{ readonly organizationId: string }> {
  const response = await fetch(`${config.hbsApiBaseUrl}/onboarding`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
      authorization: `Bearer ${body.accessToken}`,
    },
    body: JSON.stringify({ organizationName: body.organizationName, slug: body.slug }),
  });
  if (!response.ok) await bacaGalat(response);
  const hasil = parseOnboardingResult(await response.json());
  if (hasil === null) {
    throw new HbsError(response.status, 'HBS tidak mengembalikan organisasi yang dapat dipakai.');
  }
  return hasil;
}
